import { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Check,
  ShieldPlus,
  CircleDollarSign,
  Lightbulb,
  Zap,
} from "lucide-react";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";
import ContactForm from "@/components/site/ContactForm";
import Reveal from "@/components/site/Reveal";
import { findProgram, PROGRAMS } from "@/data/programs";
import { PROGRAM_DETAIL } from "@/constants/testIds";

const ICONS = { ShieldPlus, CircleDollarSign, Lightbulb, Zap };
const IMAGES = {
  "wellness-plan": "/images/program-wellness.png",
  "tariff-refund": "/images/program-tariff.png",
  "rd-tax-credits": "/images/program-rnd.png",
  "power-plan": "/images/program-power.png",
};

export default function ProgramDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const program = findProgram(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  if (!program) {
    return (
      <>
        <Navigation />
        <main className="container-narrow section-pad" style={{ textAlign: "center" }}>
          <h1 className="type-display-large mb-4">Program not found</h1>
          <button onClick={() => navigate("/")} className="btn-primary">
            Back to home
          </button>
        </main>
        <Footer />
      </>
    );
  }

  const Icon = ICONS[program.icon] || ShieldPlus;
  const others = PROGRAMS.filter((p) => p.slug !== program.slug);
  const heroImage = IMAGES[program.slug];

  return (
    <>
      <Navigation />
      <main data-testid={PROGRAM_DETAIL.root}>
        {/* Hero */}
        <section
          className="relative section-pad"
          style={{
            paddingBottom: 64,
            background: "var(--color-bg-soft)",
            overflow: "hidden",
          }}
        >
          <div className="container-wide grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <Link
                to="/#programs"
                data-testid={PROGRAM_DETAIL.back}
                className="link-arrow mb-6 inline-flex"
              >
                <ArrowLeft size={14} /> All programs
              </Link>
              <div className="flex items-center gap-3 mb-6">
                <span
                  className="flex items-center justify-center"
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 8,
                    background: "rgba(83,58,253,0.1)",
                    color: "var(--color-purple)",
                  }}
                >
                  <Icon size={24} strokeWidth={1.5} />
                </span>
                <div className="eyebrow">SmartEdge program</div>
              </div>
              <h1
                className="type-display-large mb-4"
                data-testid={PROGRAM_DETAIL.title}
                style={{ maxWidth: 560 }}
              >
                {program.title}
              </h1>
              <p className="type-body-large" style={{ maxWidth: 560 }}>
                {program.detail.lede}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  type="button"
                  data-testid={PROGRAM_DETAIL.cta}
                  className="btn-primary"
                  onClick={() =>
                    window.open(
                      "https://calendly.com/dpaul-smartedgebizsolutions/introduction-to-smart-business-solutions-with-dan-paul",
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                >
                  See if you qualify
                </button>
                <Link to="/#programs" className="btn-ghost">
                  Compare programs
                </Link>
              </div>
            </Reveal>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.4, 0, 0.2, 1] }}
              className="se-card"
              style={{ padding: 0, overflow: "hidden" }}
            >
              <div className="se-card__media" style={{ aspectRatio: "16 / 10" }}>
                <img src={heroImage} alt={program.title} />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Highlights + Process */}
        <section className="section-pad">
          <div className="container-narrow grid grid-cols-1 lg:grid-cols-2 gap-14">
            <Reveal>
              <div className="eyebrow mb-4">What you get</div>
              <h2 className="type-section-heading mb-8">Highlights</h2>
              <ul className="flex flex-col gap-4">
                {program.detail.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3">
                    <span
                      style={{
                        marginTop: 4,
                        width: 22,
                        height: 22,
                        borderRadius: 999,
                        background: "rgba(21,190,83,0.14)",
                        color: "var(--color-success-text)",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Check size={13} strokeWidth={2.5} />
                    </span>
                    <span className="type-body" style={{ color: "var(--color-label)" }}>
                      {h}
                    </span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="eyebrow mb-4">How it works</div>
              <h2 className="type-section-heading mb-8">Our process</h2>
              <ol className="flex flex-col gap-6">
                {program.detail.how.map((step, i) => (
                  <li key={step} className="flex items-start gap-4">
                    <span
                      className="timeline__num"
                      style={{ width: 40, height: 40, fontSize: 14 }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span
                      className="type-body"
                      style={{ color: "var(--color-label)", paddingTop: 8 }}
                    >
                      {step}
                    </span>
                  </li>
                ))}
              </ol>
            </Reveal>
          </div>
        </section>

        {/* Contact */}
        <section
          id="contact"
          className="section-pad"
          style={{ background: "var(--color-bg-soft)" }}
        >
          <div className="container-narrow">
            <Reveal className="max-w-2xl mb-10">
              <div className="eyebrow mb-5">Talk to a specialist</div>
              <h2 className="type-section-heading mb-3">
                Book a free consultation.
              </h2>
              <p className="type-body-large">
                We&apos;ll walk through your business and show you what{" "}
                {program.title} would look like for you.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <ContactForm defaultInterest={program.title} />
            </Reveal>
          </div>
        </section>

        {/* Other programs */}
        <section className="section-pad" style={{ paddingTop: 80, paddingBottom: 112 }}>
          <div className="container-wide">
            <Reveal className="mb-10">
              <div className="eyebrow mb-5">More programs</div>
              <h2 className="type-section-heading">Keep exploring.</h2>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {others.map((p, i) => {
                const OtherIcon = ICONS[p.icon] || ShieldPlus;
                return (
                  <Reveal key={p.slug} delay={i * 0.08}>
                    <Link to={`/programs/${p.slug}`} className="se-card h-full">
                      <div className="se-card__media">
                        <img src={IMAGES[p.slug]} alt={p.title} loading="lazy" />
                      </div>
                      <div className="se-card__body">
                        <div
                          className="flex items-center justify-center mb-4"
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 6,
                            background: "rgba(83,58,253,0.08)",
                            color: "var(--color-purple)",
                          }}
                        >
                          <OtherIcon size={18} strokeWidth={1.5} />
                        </div>
                        <div className="type-subheading mb-2" style={{ fontSize: 20 }}>
                          {p.title}
                        </div>
                        <p className="type-body" style={{ flex: 1 }}>
                          {p.body}
                        </p>
                        <span className="link-arrow mt-4">Learn more →</span>
                      </div>
                    </Link>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
